import { Container, Flex, VStack } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { RewardBox } from "../components";
import { CompoundOpportunitiesSection } from "../features/stake/CompoundOpportunitiesSection";
import { StakePageHero } from "../features/stake/StakePageHero";
import { StakeStrategyContent } from "../features/stake/StakeStrategyContent";
import { StakeWithdrawalTicketsProvider } from "../features/stake/StakeWithdrawalTicketsContext";
import { useDeFiOpportunities } from "../hooks/useDeFiOpportunities";
import {
  AssetId,
  LAST_STAKE_ASSET_KEY,
  parseAssetId,
  parseStakeMode,
  StakeMode,
} from "../config/assets";
import { useAssetConfigs } from "../config/useAssetConfigs";
import { useKyrosApy } from "../hooks/useKyrosApy";
import { useJitoSOLPrice } from "../hooks/useJitoSOLPrice";
import { useJtoPrice } from "../hooks/useJtoPrice";
import { useTokenPrice } from "../hooks/useTokenPrice";
import { useTotalSupply } from "../hooks/useTotalSupply";
import { useTokensDeposited } from "../hooks/useTokensDeposited";

export const StakePage = () => {
  const { asset: assetParam } = useParams<{ asset: string }>();
  const [searchParams] = useSearchParams();
  const assetConfigs = useAssetConfigs();
  const { apy: kyrosApy, isLoading: kyrosApyLoading } = useKyrosApy();
  const { opportunities: defiOpportunities, isLoading: defiOpportunitiesLoading } =
    useDeFiOpportunities();
  const assetId = parseAssetId(assetParam) ?? "sol";
  const mode: StakeMode = parseStakeMode(searchParams.get("mode"));

  const config = assetConfigs[assetId];

  const { price: jitoSolPrice } = useJitoSOLPrice();
  const { price: jtoPrice } = useJtoPrice();
  const { price: kyrosPrice } = useTokenPrice("kyros");
  const {
    totalSupply,
    isLoading: totalSupplyLoading,
    fetchTotalSupply,
  } = useTotalSupply();
  const {
    tokensDeposited,
    isLoading: tokensDepositedLoading,
    fetchTokensDeposited,
  } = useTokensDeposited();

  const [usdcValue, setUsdcValue] = useState<number | null>(null);
  const [tvlReadyForAsset, setTvlReadyForAsset] = useState<AssetId | null>(null);

  const resetKey = `${assetId}-${mode}`;

  useEffect(() => {
    if (assetId) {
      localStorage.setItem(LAST_STAKE_ASSET_KEY, assetId);
    }
  }, [assetId]);

  useEffect(() => {
    setUsdcValue(null);
    setTvlReadyForAsset(null);
  }, [assetId]);

  useEffect(() => {
    if (!config) return;
    if (config.id === "jto" || config.id === "kyros") {
      fetchTotalSupply(config.vrtMintAddress);
    } else if (config.id === "sol") {
      fetchTokensDeposited();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.id]);

  useEffect(() => {
    if (!config || !assetId) return;

    if (config.id === "sol") {
      if (tokensDepositedLoading || tokensDeposited === null || jitoSolPrice == null) {
        return;
      }
      setUsdcValue(Number(tokensDeposited) * jitoSolPrice);
      setTvlReadyForAsset(assetId);
      return;
    }

    if (totalSupplyLoading || totalSupply === null) {
      return;
    }

    if (config.id === "jto" && jtoPrice != null) {
      setUsdcValue(Number(totalSupply) * jtoPrice);
      setTvlReadyForAsset(assetId);
    } else if (config.id === "kyros" && kyrosPrice != null) {
      setUsdcValue(Number(totalSupply) * kyrosPrice);
      setTvlReadyForAsset(assetId);
    }
  }, [
    assetId,
    config,
    jitoSolPrice,
    tokensDeposited,
    tokensDepositedLoading,
    jtoPrice,
    totalSupply,
    totalSupplyLoading,
    kyrosPrice,
  ]);

  const tvlLoading =
    !assetId ||
    tvlReadyForAsset !== assetId ||
    usdcValue === null ||
    (config?.id === "sol"
      ? tokensDepositedLoading
      : totalSupplyLoading);

  const apyLoading = kyrosApyLoading || !kyrosApy;

  const heroApy = useMemo(() => {
    if (!assetId || !kyrosApy) return null;
    switch (assetId) {
      case "sol":
        return kyrosApy.sol ?? 0;
      case "jto":
        return kyrosApy.jto ?? 0;
      case "kyros":
        return kyrosApy.kyros ?? null;
      default:
        return null;
    }
  }, [assetId, kyrosApy]);

  const formVrtApy = useMemo(() => {
    if (!assetId || !kyrosApy) return null;
    switch (assetId) {
      case "sol":
        return mode === "unstake" ? kyrosApy.solTipRouterApy ?? null : kyrosApy.sol ?? 0;
      case "jto":
        return mode === "unstake" ? kyrosApy.jtoTipRouterApy ?? null : kyrosApy.jto ?? 0;
      case "kyros":
        return kyrosApy.kyros ?? null;
      default:
        return null;
    }
  }, [assetId, mode, kyrosApy]);

  return (
    <Flex direction="column" minHeight="100vh">
      <Container maxW="600px" w="100%" flex="1" py={8}>
        <VStack spacing={8} align="stretch" w="100%">
          <StakePageHero
            assetId={assetId}
            config={config}
            tvl={usdcValue ?? 0}
            tvlLoading={tvlLoading}
            apy={heroApy}
            apyLoading={apyLoading}
            assetConfigs={assetConfigs}
          />

          <StakeWithdrawalTicketsProvider vaultAddress={config.vaultAddress}>
            <StakeStrategyContent
              config={config}
              mode={mode}
              resetKey={resetKey}
              vrtApy={formVrtApy}
            />
          </StakeWithdrawalTicketsProvider>

          {mode === "restake" && (assetId === "sol" || assetId === "jto") && (
            <CompoundOpportunitiesSection
              assetId={assetId}
              opportunities={defiOpportunities}
              isLoading={defiOpportunitiesLoading}
            />
          )}

          {mode === "restake" && config.rewards && config.rewards.length > 0 && (
            <RewardBox rewards={config.rewards} />
          )}
        </VStack>
      </Container>
    </Flex>
  );
};
